/**
 * The button look (DEC-316 / DEC-317, decided in `public/button-look-study.html`, built 2026-10-27): the pure rules
 * behind a button grid's finish, title, icon, texture, Starts and beat flash. Numbers are the study's, each tuned
 * there on the operator's real page 1; change them in both places.
 *
 * Pure on purpose: the component reads these, a smoke can check them without a page.
 */

import type { InstrumentIconId } from '../components/InstrumentIcon.tsx';

export type GridFinish = 'lit' | 'glass' | 'pads';
export type GridTitleAt = 'tl' | 'tc' | 'tr' | 'bl' | 'bc' | 'br' | 'side' | 'off';
export type GridIconAt = 'title' | 'corner' | 'above' | 'off';
export type TexPattern = 'dimples' | 'ribs-v' | 'ribs-h' | 'waves' | 'knurl' | 'rings' | 'grid' | 'diag';
export type GridTexture = 'auto' | 'smooth' | TexPattern;
export type TexSize = 'fine' | 'medium' | 'coarse';
export type GridTexSize = 'auto' | TexSize;
/** How long a pad SHOWS the wait the receiver keeps (OPButtons sends at once: DEC-317). */
export type GridStarts = 'now' | 'beat' | 'bar' | '2bar' | '4bar';
export type GridBeatFlash = 'off' | 'pulse' | 'breathe' | 'blink';

export const LOOK_DEFAULTS = {
  finish: 'lit' as GridFinish,
  titleAt: 'tl' as GridTitleAt,
  iconAt: 'title' as GridIconAt,
  texture: 'auto' as GridTexture,
  texSize: 'auto' as GridTexSize,
  texStrength: 20,
  beatFlash: 'pulse' as GridBeatFlash,
};

/** Texture by rule, from the grid's icon: [pattern, register]. Pattern = how the sound is played (dimples: hit;
 *  vertical ribs: keys; horizontal ribs: held or bowed; waves: an oscillator; knurl: stomped; rings: a one-shot;
 *  grid: steps). Register = the size: coarse for low, fine for high. */
export const TEX_OF: Partial<Record<InstrumentIconId, [TexPattern, TexSize]>> = {
  drums: ['dimples', 'coarse'], snare: ['dimples', 'medium'], cymbal: ['dimples', 'fine'], bass: ['ribs-h', 'coarse'],
  piano: ['ribs-v', 'medium'], synth: ['ribs-v', 'fine'], waveform: ['waves', 'medium'], 'guitar-electric': ['knurl', 'medium'],
  'guitar-acoustic': ['knurl', 'medium'], pedal: ['knurl', 'coarse'], sampler: ['rings', 'medium'], turntable: ['rings', 'coarse'],
  mic: ['dimples', 'fine'], vocals: ['ribs-h', 'fine'], headphones: ['dimples', 'fine'], speaker: ['dimples', 'fine'],
  saxophone: ['ribs-h', 'medium'], trumpet: ['ribs-h', 'medium'], violin: ['ribs-h', 'fine'], mixer: ['grid', 'medium'],
  midi: ['grid', 'fine'], laptop: ['grid', 'fine'], metronome: ['ribs-v', 'coarse'],
};

export const TEX_NAMES: Record<'smooth' | TexPattern, string> = {
  smooth: 'Smooth', dimples: 'Dimples', 'ribs-v': 'Vertical ribs', 'ribs-h': 'Horizontal ribs', waves: 'Waves',
  knurl: 'Knurl', rings: 'Rings', grid: 'Grid', diag: 'Diagonal',
};

/** The pattern and size a grid's pads wear: its own choices, else the icon's rule, else smooth. */
export function textureFor(icon: InstrumentIconId | 'none' | undefined, texture: GridTexture | undefined,
  texSize: GridTexSize | undefined): { pattern: 'smooth' | TexPattern; size: TexSize } {
  const auto = icon && icon !== 'none' ? TEX_OF[icon] : undefined;
  const t = texture ?? 'auto';
  const pattern = t === 'auto' ? (auto?.[0] ?? 'smooth') : t;
  const s = texSize ?? 'auto';
  return { pattern, size: s === 'auto' ? (auto?.[1] ?? 'medium') : s };
}

/** Repeats across a pad at each size: the texture scales WITH the pad, so a small pad and a big one read alike; a
 *  shape needs more room than a line (a wave's period, a ring's spacing); a grain is a property of the material, so it
 *  stops at 13 px on a big button and never goes under 5. */
const TEX_ACROSS: Record<TexSize, number> = { fine: 7, medium: 4.8, coarse: 3.4 };
const TEX_ROOM: Partial<Record<TexPattern, number>> = { waves: 1.4, rings: 1.2 };
export function texturePx(side: number, pattern: TexPattern, size: TexSize): number {
  return Math.max(5, Math.min(13, Math.round(side / TEX_ACROSS[size] * (TEX_ROOM[pattern] ?? 1))));
}

/** Starts, in beats (lines counted from the song's start, as Live's launch quantisation counts them). */
export const STARTS_BEATS: Record<GridStarts, number> = { now: 0, beat: 1, bar: 4, '2bar': 8, '4bar': 16 };

/** The next line at or after `beat` for a wait of `len` beats. */
export function nextLine(beat: number, len: number): number {
  return Math.floor(beat / len + 1e-9) * len + len;
}

/** Beats between flashes: every beat, but never more than 3 flashes a second (DEC-262), so past 180 BPM every other
 *  beat, and so on. */
export function flashEveryBeats(bpm: number): number {
  let n = 1;
  while (bpm / 60 / n > 3) n *= 2;
  return n;
}

/** How bright a lit pad is at song beat `beat` (1 = as drawn). Pulse jumps on the line and falls away; Breathe swells
 *  to the line and back; Blink is on for the first half, as a queued clip blinks on an APC40. */
export function flashBrightness(style: GridBeatFlash, beat: number, bpm: number): number {
  if (style === 'off') return 1;
  const n = flashEveryBeats(bpm);
  const start = Math.floor(beat / n + 1e-9) * n;
  const ph = (beat - start) / n;
  const f = style === 'blink' ? (ph < 0.5 ? 1 : 0)
    : style === 'breathe' ? 0.5 + 0.5 * Math.cos(2 * Math.PI * ph)
      : Math.pow(1 - ph, 3);
  const lo = style === 'blink' ? 0.45 : 0.86, hi = 1.06;
  return lo + (hi - lo) * f;
}
